<template>
	<div class="pas">
		<div v-if="!embedded" class="field-eyebrow">
			Cuentas de remuneraciones
			<span v-if="locked" class="field-eyebrow-note">bloqueado</span>
		</div>
		<p class="pas-hint">
			Se listan solo las cuentas que alimenta el mayor. Marca las que traen
			las remuneraciones: en los meses en que el cliente sube su planilla
			pasan a <strong>Ajuste Remuneraciones</strong> y cada documento
			clasificado la descuenta, así el total del mes sigue siendo el del
			mayor. Las cuentas RR HH creadas para la planilla no aparecen aquí.
		</p>
		<p v-if="locked" class="pas-hint">
			Cuentas bloqueadas junto con el plan de cuentas.
		</p>

		<p v-if="!accounts.length" class="pas-empty">
			El mayor todav&iacute;a no alimenta cuentas de costos ni gastos. Importa el
			mayor y vuelve a esta secci&oacute;n.
		</p>

		<!-- Buscador + filtro «Solo RR HH» (Steve, 2026-10-14): con planes de
			decenas de cuentas, encontrar las de remuneraciones a ojo es lento. -->
		<div v-if="accounts.length" class="pas-tools">
			<input
				v-model.trim="query"
				type="search"
				class="input pas-search"
				placeholder="Buscar cuenta o nivel 2"
				aria-label="Buscar cuenta"
			/>
			<button
				type="button"
				class="pas-chip"
				:class="{ 'pas-chip--on': onlyRrhh }"
				:aria-pressed="onlyRrhh ? 'true' : 'false'"
				@click="onlyRrhh = !onlyRrhh"
			>
				Solo RR HH
			</button>
			<span class="pas-tools-count">{{ visibleLabel }}</span>
		</div>

		<p v-if="accounts.length && !visibleAccounts.length" class="pas-empty">
			Ninguna cuenta coincide con el filtro.
		</p>

		<div v-for="group in groups" :key="group.level_2" class="pas-group">
			<div class="pas-group-head">
				<span class="pas-group-name">{{ group.level_2 }}</span>
				<span class="pas-group-l1">{{ group.level_1 }}</span>
			</div>
			<ul class="pas-accounts">
				<li v-for="account in group.accounts" :key="account.id" class="pas-account">
					<label class="pas-check">
						<input
							type="checkbox"
							:value="account.id"
							:checked="isSelected(account.id)"
							:disabled="saving || locked"
							@change="toggle(account.id)"
						/>
						<span class="pas-name">{{ account.level_3 }}</span>
						<span v-if="account.rrhh_name" class="pas-tag">RR HH</span>
					</label>
				</li>
			</ul>
		</div>

		<p v-if="accounts.length && !selected.length && !isFiltered" class="pas-note">
			Sin cuentas marcadas, la planilla que suba el cliente no reemplaza nada
			del mayor y suma como un gasto m&aacute;s. Si el mayor ya trae
			remuneraciones, marca sus cuentas para no contarlas dos veces.
		</p>

		<p v-if="outsideCount" class="pas-warning">
			{{ outsideLabel }} de la planilla
			{{ outsideCount === 1 ? "está clasificado" : "están clasificados" }} en
			cuentas sin <strong>RR HH</strong> en el nombre. Desde ahora la planilla
			solo se puede clasificar en cuentas RR HH; reclasifica esos documentos
			cuando puedas.
		</p>

		<div v-if="!embedded && !locked && accounts.length" class="pas-actions">
			<button
				v-if="dirty"
				type="button"
				class="theme_btn_white"
				:disabled="saving"
				@click="reset"
			>
				Descartar cambios
			</button>
			<button
				type="button"
				class="theme_btn_dark"
				:disabled="!dirty || saving"
				@click="save"
			>
				{{ saving ? "Guardando…" : "Guardar" }}
			</button>
		</div>
	</div>
</template>

<script>
// Cuentas de remuneraciones del mayor (decisiones §40, 2026-10-14): el
// analista marca qué cuentas de costos y gastos de la empresa traen las
// remuneraciones; el reporte las netea contra la planilla que sube el
// cliente. Presentacional como `CvUnblockSection`: recibe la configuración
// por props y emite `save` con la lista completa de ids; la vista habla con
// la API. Sin marca por defecto: la decisión es siempre del analista.
function normalize(text) {
	return String(text || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();
}

export default {
	name: "PayrollAccountsSection",
	props: {
		// [{ id, level_1, level_2, level_3, is_payroll, rrhh_name }] en el
		// orden del plan; `rrhh_name` dice si el nombre admite planilla.
		accounts: { type: Array, default: () => [] },
		markedIds: { type: Array, default: () => [] },
		// Documentos de planilla clasificados en cuentas sin «RR HH» (E3).
		outsideCount: { type: Number, default: 0 },
		saving: { type: Boolean, default: false },
		// Candado del plan de cuentas (§30): solo lectura.
		locked: { type: Boolean, default: false },
		// Dentro de un modal: sin eyebrow ni botones propios; el contenedor
		// escucha `change` (lista completa, ordenada) y guarda desde su footer.
		embedded: { type: Boolean, default: false },
	},
	data() {
		return {
			// Copia local editable; `markedIds` sigue siendo la verdad.
			selected: [...this.markedIds],
			query: "",
			onlyRrhh: false,
		};
	},
	computed: {
		// Filtro por texto (cuenta o nivel 2, sin acentos ni mayúsculas) y por
		// nombre «RR HH». Solo afecta lo que se ve: la selección se conserva.
		visibleAccounts() {
			const needle = normalize(this.query);
			return this.accounts.filter((account) => {
				if (this.onlyRrhh && !account.rrhh_name) return false;
				if (!needle) return true;
				return normalize(account.level_3).includes(needle)
					|| normalize(account.level_2).includes(needle);
			});
		},
		visibleLabel() {
			const total = this.accounts.length;
			const shown = this.visibleAccounts.length;
			return shown === total ? `${total} cuentas` : `${shown} de ${total} cuentas`;
		},
		groups() {
			const groups = [];
			this.visibleAccounts.forEach((account) => {
				const last = groups[groups.length - 1];
				if (last && last.level_2 === account.level_2) {
					last.accounts.push(account);
				} else {
					groups.push({
						level_2: account.level_2,
						level_1: account.level_1,
						accounts: [account],
					});
				}
			});
			return groups;
		},
		dirty() {
			return this.sorted(this.selected).join(",") !== this.sorted(this.markedIds).join(",");
		},
		isFiltered() {
			return Boolean(this.query) || this.onlyRrhh;
		},
		outsideLabel() {
			const total = this.outsideCount || 0;
			return `${total.toLocaleString("es-CL")} ${total === 1 ? "documento" : "documentos"}`;
		},
	},
	watch: {
		// La vista repone la lista tras guardar (o al recargar la sección).
		markedIds(ids) {
			this.selected = [...ids];
			this.$emit("change", this.sorted(this.selected));
		},
	},
	methods: {
		isSelected(id) {
			return this.selected.indexOf(id) !== -1;
		},
		toggle(id) {
			this.selected = this.isSelected(id)
				? this.selected.filter((selectedId) => selectedId !== id)
				: this.selected.concat(id);
			this.$emit("change", this.sorted(this.selected));
		},
		reset() {
			this.selected = [...this.markedIds];
			this.$emit("change", this.sorted(this.selected));
		},
		save() {
			this.$emit("save", this.sorted(this.selected));
		},
		sorted(ids) {
			return [...ids].sort((a, b) => a - b);
		},
	},
};
</script>

<style lang="scss" scoped>
@use "./account_plan_buttons" as *;

.pas-hint {
	margin: 0 0 10px;
	font-size: 12.5px;
	line-height: 18px;
	color: var(--ap-slate);
}

.pas-empty {
	margin: 0;
	font-size: 12.5px;
	line-height: 18px;
	color: var(--ap-muted);
}

.pas-tools {
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0 0 10px;
}

.pas-search {
	flex: 1;
	min-width: 0;
	height: 32px;
	font-size: 13px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	color: var(--ap-ink);
	box-shadow: none;
	transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

	&:focus,
	&:focus-visible {
		border-color: var(--ap-teal);
		box-shadow: 0 0 0 3px var(--ap-teal-wash);
	}
}

.pas-chip {
	flex-shrink: 0;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	background: var(--ap-paper);
	color: var(--ap-ink-2);
	padding: 6px 14px;
	font: inherit;
	font-size: 12.5px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.16s var(--ap-ease), color 0.16s var(--ap-ease),
		border-color 0.16s var(--ap-ease);

	&--on {
		background: var(--ap-teal);
		border-color: var(--ap-teal-deep);
		color: var(--ap-paper);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

.pas-tools-count {
	flex-shrink: 0;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	color: var(--ap-muted);
}

@media (prefers-reduced-motion: reduce) {
	.pas-search,
	.pas-chip {
		transition: none;
	}
}

.pas-group + .pas-group {
	margin-top: 10px;
}

.pas-group-head {
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin-bottom: 2px;
}

.pas-group-name {
	font-size: 12px;
	font-weight: 600;
	color: var(--ap-ink-2);
}

.pas-group-l1 {
	font-size: 10.5px;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.pas-accounts {
	margin: 0;
	padding: 0;
	list-style: none;
}

.pas-account {
	border-top: 1px solid var(--ap-line);
}

.pas-account:last-child {
	border-bottom: 1px solid var(--ap-line);
}

.pas-check {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 7px 0;
	cursor: pointer;
}

.pas-check input:disabled + .pas-name {
	color: var(--ap-muted);
}

.pas-name {
	flex: 1;
	min-width: 0;
	font-size: 13px;
	color: var(--ap-ink-2);
	overflow-wrap: anywhere;
}

.pas-tag {
	flex: none;
	padding: 1px 7px;
	font-size: 10.5px;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: var(--ap-teal-deep);
	background-color: var(--ap-teal-wash);
	border-radius: var(--ap-radius-pill);
}

.pas-note {
	margin: 10px 0 0;
	font-size: 12.5px;
	line-height: 18px;
	color: var(--ap-slate);
}

.pas-warning {
	margin: 10px 0 0;
	padding: 8px 10px;
	font-size: 12.5px;
	line-height: 18px;
	color: var(--ap-warn-ink);
	background-color: var(--ap-warn-wash);
	border-radius: var(--ap-radius-sm);
}

.pas-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 12px;
}
</style>
